import {
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import { RootState } from "..";
import { gatewayApi } from "../../api";
import { AppSession, appSession } from "../../shared/lib/storage";
import { toStaffIdentity } from "../../shared/auth";

type AuthState = {
  user: AppSession | null;
  loading: "idle" | "pending" | "succeeded" | "failed";
};

const initialState = (): AuthState => ({
  user: appSession.getItem() || null,
  loading: "idle",
});

export const login = createAsyncThunk(
  "auth/login",
  async (
    { username, password }: { username: string; password: string },
    { dispatch },
  ) => {
    const { result } = await dispatch(
      gatewayApi.endpoints.login.initiate({ username, password }),
    ).unwrap();

    // Persist the tokens before requesting /staff/me so its request interceptor
    // can authenticate the identity lookup. Identity enrichment is best effort:
    // the session remains usable during a temporary identity endpoint outage.
    appSession.removeItem();
    appSession.setItem(result);

    try {
      const { result: staff } = await dispatch(
        gatewayApi.endpoints.staffMe.initiate(undefined, {
          forceRefetch: true,
          subscribe: false,
        }),
      ).unwrap();

      return { ...result, staff: toStaffIdentity(staff) } satisfies AppSession;
    } catch {
      return result satisfies AppSession;
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch }) => {
    const session = appSession.getItem();
    const result = await dispatch(
      gatewayApi.endpoints.logout.initiate({
        refresh_token: session?.refresh_token || "",
      }),
    ).unwrap();

    return result;
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearSession: (state) => {
      state.user = null;
      state.loading = "idle";
    },
  },
  selectors: {
    selectIsAuthenticated: (state) =>
      !!state.user && state.loading !== "pending",
  },

  extraReducers(builder) {
    builder.addCase(login.fulfilled, (state, action) => {
      state.user = action.payload;
      state.loading = "succeeded";
    });
    builder.addCase(login.rejected, (state) => {
      state.user = null;
      state.loading = "failed";
    });
    builder.addCase(login.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      state.loading = "idle";
    });
  },
});

export const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  matcher: isAnyOf(
    login.fulfilled,
    logout.fulfilled,
    authSlice.actions.clearSession,
  ),
  effect: (_, listenerApi) => {
    const state = listenerApi.getState() as RootState;

    if (state.auth.user) {
      appSession.setItem(state.auth.user);
    } else {
      appSession.removeItem();
    }
  },
});

export const { selectIsAuthenticated } = authSlice.selectors;
export const { clearSession } = authSlice.actions;

export default authSlice.reducer;
